import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { ROUTES } from "../../../../routes/routes";
import { RoleEnum } from "../../../../api/client/types/user.types";
import { useAuthStore } from "../../../../store";
import {
  useGetSwimmerById,
  useUpdateSwimmer,
  swimmerQueryKeys,
} from "../../services/swimmerService";
import { useGetSwimmerTypes } from "../../services/swimmerTypeService";
import type { EditSwimmerFormData } from "../../components/EditSwimmerForm/EditSwimmerForm.types";
import type { UpdateSwimmerRequest, Swimmer } from "../../../../api/client/types/swimmer.types";
import type { PaginatedResponse } from "../../../../api/client/types/common.types";
import { mapSwimmerToInitialData } from "./mapSwimmerToInitialData";
import { colombiaDateTimeToUTCISO } from "../../../../shared/utils/dateUtils";

export const useEditSwimmerPage = () => {
  const { swimmerId } = useParams<{ swimmerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const {
    data: swimmer,
    isLoading: isLoadingSwimmer,
    isError: isErrorSwimmer,
  } = useGetSwimmerById(swimmerId ?? "");

  const { data: swimmerTypes } = useGetSwimmerTypes();

  const { mutate: updateSwimmer, isPending: isUpdating } = useUpdateSwimmer();

  const handleNavigateToDashboard = () => {
    const isLifeguard = user?.role === RoleEnum.LIFEGUARD;
    if (isLifeguard) {
      navigate(`${ROUTES.DASHBOARD.LIFEGUARD}?tab=banistas`);
      return;
    }
    // Admin: restore tab + filters from when they left (same as edit new)
    const returnSearch =
      (location.state as { returnSearch?: string } | undefined)?.returnSearch ??
      "?tab=banistas";
    navigate(ROUTES.DASHBOARD.ADMINISTRATOR + returnSearch);
  };

  /**
   * When the updated swimmer is a dependent of another, update that responsible's
   * detail cache so the dependents list shows the new name (e.g. on delete page).
   */
  const updateResponsibleDetailDependentName = (updatedSwimmer: Swimmer) => {
    const responsibleId =
      updatedSwimmer.responsible_id ?? updatedSwimmer.responsible?.id;
    if (!responsibleId || !updatedSwimmer.id) return;

    queryClient.setQueryData<Swimmer>(
      swimmerQueryKeys.detail(responsibleId),
      (old) => {
        if (!old?.dependents?.length) return old;
        const nextDependents = old.dependents.map((d) =>
          d.id === updatedSwimmer.id
            ? { id: d.id, name: updatedSwimmer.name }
            : d,
        );
        return { ...old, dependents: nextDependents };
      },
    );
  };

  /**
   * When the updated swimmer is a responsible (has dependents), update each
   * dependent's detail cache so their `responsible` object shows the latest data
   * (e.g. when opening edit page for a minor).
   */
  const updateDependentsDetailResponsible = (updatedSwimmer: Swimmer) => {
    if (!updatedSwimmer.dependents?.length || !updatedSwimmer.id) return;

    updatedSwimmer.dependents.forEach((d) => {
      queryClient.setQueryData<Swimmer>(
        swimmerQueryKeys.detail(d.id),
        (old) => {
          if (!old) return old;
          return { ...old, responsible: updatedSwimmer };
        },
      );
    });
  };

  const buildUpdatePayload = (
    data: EditSwimmerFormData,
    registrationTimestamp?: string | null,
  ): UpdateSwimmerRequest => {
    const payload: UpdateSwimmerRequest = {
      name: data.name,
      building_tower: data.building_tower,
      building_apartment: data.building_apartment,
      swimmer_type_id: parseInt(data.swimmer_type_id, 10),
    };
    if (registrationTimestamp) {
      payload.registration_timestamp =
        colombiaDateTimeToUTCISO(registrationTimestamp);
    }
    return payload;
  };

  const handleFormSubmit = (
    data: EditSwimmerFormData,
    registrationTimestamp?: string | null,
  ) => {
    if (!swimmerId) return;
    const updatePayload = buildUpdatePayload(data, registrationTimestamp);
    const isLifeguard = user?.role === RoleEnum.LIFEGUARD;

    updateSwimmer(
      { swimmer_id: swimmerId, data: updatePayload },
      {
        onSuccess: (updatedSwimmer) => {
          if (isLifeguard) {
            // Cache update: avoid refetch for lifeguard dashboard
            queryClient.setQueryData<Swimmer>(
              swimmerQueryKeys.detail(swimmerId),
              updatedSwimmer,
            );
            queryClient.setQueriesData<PaginatedResponse<Swimmer>>(
              { queryKey: [...swimmerQueryKeys.root, "list"], exact: false },
              (old) => {
                if (!old?.items) return old;
                const hasMatch = old.items.some(
                  (s) => s.id === swimmerId || s.responsible?.id === swimmerId,
                );
                if (!hasMatch) return old;
                return {
                  ...old,
                  items: old.items.map((s) => {
                    if (s.id === swimmerId) return updatedSwimmer;
                    if (s.responsible?.id === swimmerId) {
                      return { ...s, responsible: updatedSwimmer };
                    }
                    return s;
                  }),
                };
              },
            );
          } else {
            // Admin: invalidate so tables refetch (broader filters/reports)
            queryClient.invalidateQueries({
              queryKey: [...swimmerQueryKeys.root, "list"],
            });
            queryClient.setQueryData<Swimmer>(
              swimmerQueryKeys.detail(swimmerId),
              updatedSwimmer,
            );
            queryClient.invalidateQueries({
              queryKey: [...swimmerQueryKeys.root, "reportPreview"],
            });
          }
          updateResponsibleDetailDependentName(updatedSwimmer);
          updateDependentsDetailResponsible(updatedSwimmer);
          toast.success("Bañista actualizado correctamente.");
          handleNavigateToDashboard();
        },
      },
    );
  };

  const initialData = swimmer ? mapSwimmerToInitialData(swimmer) : undefined;

  return {
    swimmerId,
    swimmer,
    swimmerTypes: swimmerTypes ?? [],
    initialData,
    isLoadingSwimmer,
    isErrorSwimmer,
    isSubmitting: isUpdating,
    handleFormSubmit,
    handleNavigateToDashboard,
  };
};
